import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Link2, MessageCircleQuestion, Mic, Paperclip, GitCompareArrows, Plus, X } from "lucide-react";
import { isUrl } from "@/lib/rexxponsApi";
import { Wordmark } from "./Wordmark";
import { CognitiveCommand } from "./CognitiveCommand";
import { NetworkContactBar } from "./NetworkContactBar";
import { Footer } from "./Footer";

const SUGGESTIONS = [
  { kind: "url", label: "Wikipedia · Artificial Intelligence", value: "https://en.wikipedia.org/wiki/Artificial_intelligence" },
  { kind: "ask", label: "What caused the 2008 financial crisis?", value: "What caused the 2008 financial crisis?" },
  { kind: "ask", label: "How do mRNA vaccines work?", value: "How do mRNA vaccines work?" },
];

export const Landing = ({ onSubmit, onUpload, onCrossref, settings, onSettingsChange, entitled, onUpgrade }) => {
  const [value, setValue] = useState("");
  const [listening, setListening] = useState(false);
  const [xrefOpen, setXrefOpen] = useState(false);
  const [xrefUrls, setXrefUrls] = useState(["", ""]);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const recognitionRef = useRef(null);
  const detected = value.trim() ? (isUrl(value) ? "url" : "ask") : null;

  const setXref = (i, v) => setXrefUrls((arr) => arr.map((u, idx) => (idx === i ? v : u)));
  const addXref = () => setXrefUrls((arr) => (arr.length < 4 ? [...arr, ""] : arr));
  const removeXref = (i) => setXrefUrls((arr) => (arr.length > 2 ? arr.filter((_, idx) => idx !== i) : arr));
  const submitXref = () => {
    const urls = xrefUrls.map((u) => u.trim()).filter(Boolean);
    if (urls.length >= 2) onCrossref(urls);
  };

  const submit = () => {
    const v = value.trim();
    if (v) onSubmit(v);
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = "";
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("Voice input isn't supported in this browser.");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      const text = Array.from(e.results).map((r) => r[0].transcript).join("");
      setValue(text);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
  };

  return (
    <>
      <div className="relative z-10 min-h-screen w-full flex flex-col px-6 pt-24 pb-16">
        <NetworkContactBar />
        <div className="flex-1 w-full flex flex-col items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="w-full max-w-3xl"
      >
        <div className="text-center mb-10">
          <span className="text-[11px] tracking-[0.28em] uppercase text-primary/80 block mb-6">The Cognitive Web Browser</span>
          <Wordmark className="text-4xl sm:text-6xl text-foreground block mb-5" />
          <p className="font-heading text-base sm:text-lg text-muted-foreground">Beyond Search, Into Understanding.</p>
        </div>

        <div className="mb-6">
          <CognitiveCommand settings={settings} onChange={onSettingsChange} entitled={entitled} onUpgrade={onUpgrade} />
        </div>

        <div className="relative">
          <div className="flex items-center gap-3 bg-secondary/70 border border-border focus-within:border-primary/60 focus-within:nexx-glow rounded-2xl px-5 py-4 transition-colors">
            <span className="text-muted-foreground shrink-0">
              {detected === "url" ? (
                <Link2 strokeWidth={1.5} className="w-5 h-5 text-primary" />
              ) : (
                <MessageCircleQuestion strokeWidth={1.5} className="w-5 h-5" />
              )}
            </span>
            <input
              ref={inputRef}
              data-testid="nexx-input"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Paste a link to interpret — or ask a question…"
              className="flex-1 min-w-0 bg-transparent outline-none border-none text-base sm:text-lg text-foreground placeholder:text-muted-foreground/60 caret-cyan"
            />
            <button
              data-testid="attach-file-btn"
              onClick={() => fileRef.current?.click()}
              aria-label="Attach document"
              className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
            >
              <Paperclip strokeWidth={1.5} className="w-5 h-5" />
            </button>
            <input
              ref={fileRef}
              data-testid="file-input"
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={onFilePicked}
              className="hidden"
            />
            <button
              data-testid="voice-input-btn"
              onClick={toggleVoice}
              aria-label="Voice input"
              className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border transition-colors ${
                listening ? "border-primary bg-primary/15 text-primary animate-pulse" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              <Mic strokeWidth={1.5} className="w-5 h-5" />
            </button>
            <button
              data-testid="nexx-submit-btn"
              onClick={submit}
              disabled={!value.trim()}
              aria-label="Go"
              className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-primary text-primary-foreground disabled:opacity-30 active:scale-95 hover:bg-primary/90 transition-[background-color,transform]"
            >
              <ArrowRight strokeWidth={2} className="w-5 h-5" />
            </button>
          </div>
          {detected && (
            <p data-testid="mode-hint" className="mt-3 text-xs text-muted-foreground text-center">
              {detected === "url"
                ? "Detected a link → opening the Cognitive Reader (Read · Interpret · Explore)"
                : "Detected a question → running NexxSearch synthesis"}
            </p>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-2.5 justify-center">
          {SUGGESTIONS.map((s) => (
            <button
              key={s.value}
              data-testid="suggestion-chip"
              onClick={() => onSubmit(s.value)}
              className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground hover:text-foreground border border-border hover:border-primary/40 rounded-full px-3.5 py-2 transition-colors"
            >
              {s.kind === "url" ? (
                <Link2 strokeWidth={1.5} className="w-3.5 h-3.5 text-primary/70" />
              ) : (
                <MessageCircleQuestion strokeWidth={1.5} className="w-3.5 h-3.5" />
              )}
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-6 flex justify-center">
          <button
            data-testid="crossref-toggle"
            onClick={() => setXrefOpen((o) => !o)}
            className={`flex items-center gap-2 text-xs tracking-[0.14em] uppercase transition-colors ${
              xrefOpen ? "text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <GitCompareArrows strokeWidth={1.5} className="w-4 h-4" /> Cross-reference multiple sources
          </button>
        </div>

        {xrefOpen && (
          <div data-testid="crossref-panel" className="mt-5 rounded-2xl border border-border bg-secondary/40 p-5">
            <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Compare 2–4 sources to map agreements & friction points
            </p>
            <div className="space-y-3">
              {xrefUrls.map((u, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Link2 strokeWidth={1.5} className="w-4 h-4 text-muted-foreground shrink-0" />
                  <input
                    data-testid={`crossref-url-${i}`}
                    value={u}
                    onChange={(e) => setXref(i, e.target.value)}
                    placeholder={`https://source-${i + 1}.com/article`}
                    className="flex-1 bg-background border border-border focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground outline-none caret-cyan"
                  />
                  {xrefUrls.length > 2 && (
                    <button data-testid={`crossref-remove-${i}`} onClick={() => removeXref(i)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors">
                      <X strokeWidth={1.5} className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4">
              <button
                data-testid="crossref-add-url"
                onClick={addXref}
                disabled={xrefUrls.length >= 4}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
              >
                <Plus strokeWidth={2} className="w-3.5 h-3.5" /> Add source
              </button>
              <button
                data-testid="crossref-submit-btn"
                onClick={submitXref}
                disabled={xrefUrls.filter((u) => u.trim()).length < 2}
                className="flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium disabled:opacity-30 hover:bg-primary/90 active:scale-95 transition-[background-color,transform]"
              >
                <GitCompareArrows strokeWidth={1.75} className="w-4 h-4" /> Cross-reference
              </button>
            </div>
          </div>
        )}
      </motion.div>
        </div>
      </div>
      <Footer />
    </>
  );
};
